import { useCreation } from 'ahooks';
import type { SelectedAsset } from '@/app/types';
import type { CommunityState } from '../types';
import type { useFetchData } from './use-fetch-data';

interface UsePublishedDetailProps {
  shortFilmData: ReturnType<typeof useFetchData>;
  tutorialData: ReturnType<typeof useFetchData>;
  inspirationData: ReturnType<typeof useFetchData>;
}

/**
 * 管理详情弹窗的配置和状态
 */
export const usePublishedDetail = ({ shortFilmData, tutorialData, inspirationData }: UsePublishedDetailProps) => {
  const createActionConfig = (data: ReturnType<typeof useFetchData>) => ({
    onPrev: () => data.onNavigate('prev'),
    prevDisabled: data.isPrevButtonDisabled,
    onNext: () => data.onNavigate('next'),
    nextDisabled: data.isNextButtonDisabled,
  });

  const createDetailConfig = (selectedAsset: SelectedAsset, data: ReturnType<typeof useFetchData>) => {
    return {
      detail: {
        ...selectedAsset,
        open: true,
        onClose: () => {
          data.handleLeave();
          data.setState({ selectedAsset: undefined } as unknown as Pick<CommunityState, keyof CommunityState>);
        },
        onVideoPlayEnd: data.incrementCompleteTimes,
        handleVote: data.vote,
        handleUnVote: data.unVote,
        handleCollect: data.collect,
        handleUnCollect: data.unCollect,
        thumbScrollListConfig: {
          list: data.state.list,
          hasMore: data.state.hasMore,
          onLoadMore: () => data.fetchReq.run(),
        },
      },
      action: createActionConfig(data),
    };
  };

  const detailConfig = useCreation(() => {
    // 短片详情
    if (shortFilmData.state.selectedAsset) {
      return createDetailConfig(shortFilmData.state.selectedAsset, shortFilmData);
    }

    // 教程详情
    if (tutorialData.state.selectedAsset) {
      return createDetailConfig(tutorialData.state.selectedAsset, tutorialData);
    }

    // 灵感详情
    if (inspirationData.state.selectedAsset) {
      return createDetailConfig(inspirationData.state.selectedAsset, inspirationData);
    }

    return null;
  }, [
    shortFilmData.state.selectedAsset,
    tutorialData.state.selectedAsset,
    inspirationData.state.selectedAsset,
    shortFilmData,
    tutorialData,
    inspirationData,
  ]);

  return { detailConfig };
};
